import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  Network, Cpu, Mobile, BarChart3, Truck,
  Shield, Clock, Zap, Map, Radio, MessageSquare,
  Navigation, Settings, RefreshCw, Smartphone,
  Cloud, Brain, Route, Box,
  LineChart, Bell, CheckCircle2, Boxes,
  Signal, Battery, Smartphone as AndroidIcon, ArrowRight, Phone, Mail, MapPin,
} from 'lucide-react';
import worldMap from '../assets/images/earth_lights_lrg.jpg';
import { Link } from 'react-router-dom';

// Reusable components similar to other pages
const ServiceCard = ({ icon: Icon, title, description, features }) => (
  <motion.div
    className="relative group"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
  >
    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-indigo-900/50 to-purple-900/50 p-1">
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 blur-xl" />
      <div className="relative bg-gray-900 p-6 rounded-lg flex flex-col items-center text-center">
        <div className="mb-4 inline-block p-3 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-lg">
          <Icon className="w-8 h-8 text-indigo-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-4">{title}</h3>
        <p className="text-gray-300 mb-6">{description}</p>
        <ul className="space-y-3">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start space-x-3">
              <CheckCircle2 className="w-5 h-5 text-indigo-400 mt-0.5 flex-shrink-0" />
              <span className="text-gray-300">{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </motion.div>
);

// Network Activity Visualization
const NetworkActivity = () => {
  // Convert lat/long to x/y percentages for our map projection
  // For longitude: -180 to +180 maps to 0% to 100% (x-axis)
  // For latitude: +90 to -90 maps to 0% to 100% (y-axis)
  const convertCoordinates = (lat, long) => {
    // Add 180 to longitude to make it 0-360, then divide by 360 to get percentage
    const x = ((long + 180) / 360) * 100;
    // Subtract latitude from 90 to invert it, divide by 180 to get percentage
    const y = ((90 - lat) / 180) * 100;
    return { x, y };
  };

  // Define nodes with actual geographical coordinates
  const nodes = [
    { 
      id: 0, 
      ...convertCoordinates(40.7047955, -74.0076151),
      type: 'hub', 
      name: 'New York'
    },
    { 
      id: 1, 
      ...convertCoordinates(51.5074, -0.1641),
      type: 'hub', 
      name: 'London'
    },
    { 
      id: 2, 
      ...convertCoordinates(25.2048, 55.2708),
      type: 'hub', 
      name: 'Dubai'
    },
    { 
      id: 3, 
      ...convertCoordinates(1.3521, 103.8198),
      type: 'hub', 
      name: 'Singapore',
      desktopOnly: true 
    },
    { 
      id: 4, 
      ...convertCoordinates(-23.5558, -46.6396),
      type: 'hub', 
      name: 'Sao Paulo',
      desktopOnly: true 
    },
    { 
      id: 5, 
      ...convertCoordinates(35.6595, 139.7006),
      type: 'hub', 
      name: 'Tokyo',
      desktopOnly: true 
    }
  ];

  const [visibleNodes, setVisibleNodes] = useState([]);
  const [connections, setConnections] = useState([]);

  useEffect(() => {
    // Filter visible nodes based on screen size
    const isMobile = window.innerWidth < 768;
    const filteredNodes = nodes.filter(node => !isMobile || !node.desktopOnly);
    setVisibleNodes(filteredNodes);

    // Update connections based on visible nodes
    const newConnections = isMobile ? [
      { source: nodes[0], target: nodes[1], volume: 0.9 },
      { source: nodes[1], target: nodes[2], volume: 0.7 },
    ] : [
      { source: nodes[0], target: nodes[1], volume: 0.9 }, // NYC to London
      { source: nodes[1], target: nodes[2], volume: 0.7 }, // London to Dubai
      { source: nodes[2], target: nodes[3], volume: 0.8 }, // Dubai to Singapore
      { source: nodes[3], target: nodes[5], volume: 0.9 }, // Singapore to Tokyo
      { source: nodes[0], target: nodes[4], volume: 0.6 }, // NYC to Sao Paulo
      { source: nodes[5], target: nodes[0], volume: 0.8 }, // Tokyo to NYC
    ];
    
    setConnections(newConnections);

    // Add resize listener
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      setVisibleNodes(nodes.filter(node => !isMobile || !node.desktopOnly));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="relative w-full" style={{ paddingBottom: '50%' }}>
      <div className="absolute inset-0 rounded-3xl overflow-hidden">
        {/* World map background */}
        <div 
          className="absolute inset-0 bg-center bg-no-repeat opacity-70"
          style={{ 
            backgroundImage: `url(${worldMap})`,
            backgroundSize: 'contain'
          }}
        />
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 via-transparent to-gray-900/50" />

        {/* Hub nodes with fixed positioning */}
        {visibleNodes.map((node) => (
          <motion.div
            key={node.id}
            className="absolute"
            style={{ 
              left: `${node.x}%`, 
              top: `${node.y}%`,
              transform: 'translate(-50%, -50%)'
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Node dot */}
            <div className="relative">
              <div className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-indigo-500">
                <div className="absolute inset-0 rounded-full animate-ping bg-indigo-400 opacity-20" />
              </div>
              {/* City name label */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 md:mt-2 text-[10px] md:text-xs text-white font-medium whitespace-nowrap">
                {node.name}
              </div>
            </div>
          </motion.div>
        ))}

        {/* Connection lines */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none">
          {connections.map((connection, index) => (
            <motion.line
              key={index}
              x1={`${connection.source.x}%`}
              y1={`${connection.source.y}%`}
              x2={`${connection.target.x}%`}
              y2={`${connection.target.y}%`}
              stroke="rgba(99, 102, 241, 0.4)"
              strokeWidth="2"
              strokeDasharray="4 4"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 0.6 }}
              transition={{ duration: 1.5, delay: index * 0.2 }}
            />
          ))}
        </svg>
      </div>
    </div>
  );
};

// Enhanced AI Visualization Component
const AIVisualization = () => {
  const [network, setNetwork] = useState({ layers: [] });
  
  useEffect(() => {
    // Create a more realistic neural network structure
    const generateNetwork = () => {
      const layers = [
        { size: 3, name: 'Input' },
        { size: 6, name: 'Hidden 1' },
        { size: 8, name: 'Hidden 2' },
        { size: 2, name: 'Output' }
      ];

      let nodes = [];
      let nodeId = 0;

      layers.forEach((layer, layerIndex) => {
        const layerNodes = [];
        for (let i = 0; i < layer.size; i++) {
          layerNodes.push({
            id: nodeId++,
            x: 25 + (layerIndex * 50) / (layers.length - 1),
            y: 50 + ((i - (layer.size - 1) / 2) * 5),
            layer: layerIndex,
            activation: Math.random()
          });
        }
        nodes = [...nodes, ...layerNodes];
      });

      return { layers: nodes };
    };

    setNetwork(generateNetwork());

    const interval = setInterval(() => {
      setNetwork(prev => ({
        ...prev,
        layers: prev.layers.map(node => ({
          ...node,
          activation: Math.random()
        }))
      }));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative h-[500px] md:h-[700px] bg-gray-900/50 rounded-3xl overflow-hidden backdrop-blur-sm">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgba(139,92,246,0.15),transparent_70%)]" />
      
      {/* Nodes */}
      {network.layers.map((node) => (
        <motion.div
          key={node.id}
          className="absolute -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${node.x}%`, top: `${node.y}%` }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
        >
          <motion.div 
            className={`relative w-4 h-4 rounded-full ${
              node.layer === 0 ? 'bg-violet-500' :
              node.layer === network.layers.length - 1 ? 'bg-indigo-500' :
              'bg-purple-500'
            }`}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 1, 0.5]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: Math.random() * 2
            }}
          >
            <div className="absolute inset-0 rounded-full animate-ping opacity-20" />
          </motion.div>
        </motion.div>
      ))}

      {/* Layer labels */}
      <div className="absolute top-4 left-4 space-y-2">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-violet-500" />
          <span className="text-white text-sm">Input Layer</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-purple-500" />
          <span className="text-white text-sm">Hidden Layers</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-indigo-500" />
          <span className="text-white text-sm">Output Layer</span>
        </div>
      </div>
    </div>
  );
};

// Mobile App Preview
const MobileAppPreview = () => {
  const [activeScreen, setActiveScreen] = useState(0);
  const swipeConfidenceThreshold = 10000;
  const dragConstraints = { left: 0, right: 0 }; // Prevent vertical dragging
  
  const screens = [
    {
      title: 'Real-time Tracking',
      icon: Map,
      content: (
        <div className="h-full space-y-4">
          {/* Simplified map view */}
          <div className="h-3/5 rounded-xl bg-indigo-900/50 relative overflow-hidden">
            <div className="absolute inset-0 opacity-50 bg-[url('data:image/svg+xml,...')]" />
            {/* Route line */}
            <div className="absolute left-1/4 right-1/4 top-1/2 h-0.5 bg-indigo-400" />
            {/* Origin point */}
            <div className="absolute left-1/4 top-1/2 w-3 h-3 -translate-y-1/2 bg-violet-500 rounded-full">
              <div className="absolute inset-0 rounded-full animate-ping bg-violet-400 opacity-20" />
            </div>
            {/* Destination point */}
            <div className="absolute right-1/4 top-1/2 w-3 h-3 -translate-y-1/2 bg-indigo-500 rounded-full">
              <div className="absolute inset-0 rounded-full animate-ping bg-indigo-400 opacity-20" />
            </div>
            {/* Moving truck indicator */}
            <motion.div 
              className="absolute top-1/2 w-2 h-2 -translate-y-1/2 bg-green-400 rounded-full"
              animate={{ left: ['25%', '75%'] }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            />
          </div>
          {/* Status information */}
          <div className="space-y-2">
            <div className="flex justify-between items-center bg-indigo-500/10 p-2 rounded-lg">
              <span className="text-xs text-gray-300">ETA</span>
              <span className="text-xs text-white font-medium">2h 15m</span>
            </div>
            <div className="flex justify-between items-center bg-indigo-500/10 p-2 rounded-lg">
              <span className="text-xs text-gray-300">Distance</span>
              <span className="text-xs text-white font-medium">142 km</span>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Load Management',
      icon: Boxes,
      content: (
        <div className="h-full space-y-3">
          {/* Load cards */}
          {[...Array(3)].map((_, i) => (
            <motion.div 
              key={i}
              className="bg-indigo-500/10 p-3 rounded-lg space-y-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <div className="flex justify-between items-center">
                <span className="text-xs text-white font-medium">Load #{1001 + i}</span>
                <span className="text-xs text-green-400">In Transit</span>
              </div>
              <div className="flex justify-between items-center text-xs text-gray-300">
                <span>Weight: {(2 + i * 1.5).toFixed(1)}t</span>
                <span>Volume: {(8 + i * 4)}m³</span>
              </div>
            </motion.div>
          ))}
        </div>
      )
    },
    {
      title: 'Communication',
      icon: MessageSquare,
      content: (
        <div className="h-full space-y-3">
          {/* Chat messages */}
          <div className="space-y-2">
            {[
              { sent: false, text: "Package delayed at checkpoint" },
              { sent: true, text: "Acknowledged. ETA updated." },
              { sent: false, text: "New pickup request received" }
            ].map((msg, i) => (
              <motion.div
                key={i}
                className={`flex ${msg.sent ? 'justify-end' : 'justify-start'}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-xs ${
                  msg.sent ? 'bg-indigo-500' : 'bg-indigo-500/20'
                }`}>
                  {msg.text}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )
    },
    {
      title: 'Analytics',
      icon: BarChart3,
      content: (
        <div className="h-full space-y-4">
          {/* Graph visualization */}
          <div className="h-3/5 bg-indigo-500/10 rounded-lg p-3">
            <div className="h-full flex items-end space-x-2">
              {[65, 40, 85, 50, 75].map((height, i) => (
                <motion.div
                  key={i}
                  className="flex-1 bg-indigo-400"
                  initial={{ height: 0 }}
                  animate={{ height: `${height}%` }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                />
              ))}
            </div>
          </div>
          {/* Stats */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: "Deliveries", value: "24" },
              { label: "On Time", value: "96%" },
              { label: "Distance", value: "892km" },
              { label: "Fuel Used", value: "142L" }
            ].map((stat, i) => (
              <div key={i} className="bg-indigo-500/10 p-2 rounded-lg">
                <div className="text-xs text-gray-300">{stat.label}</div>
                <div className="text-sm text-white font-medium">{stat.value}</div>
              </div>
            ))}
          </div>
        </div>
      )
    }
  ];

  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };

  const paginate = (direction) => {
    const newIndex = activeScreen + direction;
    // Ensure we stay within bounds
    if (newIndex >= 0 && newIndex < screens.length) {
      setActiveScreen(newIndex);
    }
  };

  return (
    <div className="relative max-w-sm mx-auto">
      <motion.div
        className="relative rounded-[3rem] overflow-hidden bg-gray-900 border-4 border-gray-800 aspect-[9/19]"
        initial={{ y: 100, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="absolute top-0 w-full h-6 bg-gray-800">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-4 bg-gray-900 rounded-full" />
        </div>
        
        {/* Screen content with swipe functionality */}
        <motion.div 
          className="p-6 h-full bg-gradient-to-br from-gray-900 to-indigo-900/50"
          drag="x"
          dragConstraints={dragConstraints}
          dragElastic={1}
          onDragEnd={(e, { offset, velocity }) => {
            const swipe = swipePower(offset.x, velocity.x);

            if (swipe < -swipeConfidenceThreshold) {
              paginate(1);
            } else if (swipe > swipeConfidenceThreshold) {
              paginate(-1);
            }
          }}
        >
          <div className="flex items-center justify-between mb-8">
            <div className="text-white text-lg font-semibold">
              {screens[activeScreen].title}
            </div>
            <div className="flex space-x-2">
              <div className="w-4 h-4 rounded-full bg-indigo-500/20 flex items-center justify-center">
                <Signal className="w-3 h-3 text-indigo-400" />
              </div>
              <div className="w-4 h-4 rounded-full bg-indigo-500/20 flex items-center justify-center">
                <Battery className="w-3 h-3 text-indigo-400" />
              </div>
            </div>
          </div>
          
          {/* Animate screen content */}
          <motion.div
            key={activeScreen}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.3 }}
          >
            {screens[activeScreen].content}
          </motion.div>

          {/* Navigation dots */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
            {screens.map((_, index) => (
              <motion.button
                key={index}
                className={`w-2 h-2 rounded-full ${
                  index === activeScreen ? 'bg-indigo-500' : 'bg-gray-600'
                }`}
                onClick={() => setActiveScreen(index)}
                whileHover={{ scale: 1.2 }}
              />
            ))}
          </div>
        </motion.div>
      </motion.div>

      {/* Platform icons */}
      <div className="flex justify-center space-x-4 mt-6">
        <motion.div
          className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2"
          whileHover={{ scale: 1.05 }}
        >
          <Smartphone className="w-5 h-5 text-white" />
          <span className="text-white text-sm">iOS</span>
        </motion.div>
        <motion.div
          className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2"
          whileHover={{ scale: 1.05 }}
        >
          <AndroidIcon className="w-5 h-5 text-white" />
          <span className="text-white text-sm">Android</span>
        </motion.div>
      </div>
    </div>
  );
};

function Services() {
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const y = useTransform(scrollYProgress, [0, 0.2], [0, -50]);

  return (
    <div className="bg-gray-900">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-30" />
        </div>
        
        <div className="relative container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            style={{ opacity, y }}
          >
            <motion.h1 
              className="text-4xl md:text-6xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Intelligent{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                Logistics Solutions
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-xl text-gray-300 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Powered by cutting-edge technology and AI, our services revolutionize
              how goods move across the globe.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Real-time Network Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-indigo-900">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">Real-time Data Network</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our advanced network processes millions of data points in real-time,
              ensuring seamless logistics operations
            </p>
          </motion.div>

          <NetworkActivity />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            <ServiceCard
              icon={Network}
              title="Live Telemetry"
              description="Real-time vehicle and cargo monitoring"
              features={[
                "GPS tracking and geofencing",
                "Temperature and humidity monitoring",
                "Fuel efficiency tracking",
                "Real-time alerts and notifications"
              ]}
            />
            <ServiceCard
              icon={Radio}
              title="Status Updates"
              description="Instant visibility into shipment status"
              features={[
                "Automated status reporting",
                "Delay predictions and alerts",
                "Custom notification rules",
                "Stakeholder updates"
              ]}
            />
            <ServiceCard
              icon={RefreshCw}
              title="Synchronized Data"
              description="Seamless data integration across systems"
              features={[
                "API-first architecture",
                "Real-time data synchronization",
                "Multiple integration options",
                "Secure data transmission"
              ]}
            />
          </div>
        </div>
      </section>

      {/* AI Solutions Section */}
      <section className="py-20 bg-gradient-to-b from-indigo-900 to-purple-900">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Neural Network Intelligence
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our deep learning systems process millions of logistics decisions daily, continuously evolving to optimize your supply chain
            </p>
          </motion.div>

          <AIVisualization />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            <ServiceCard
              icon={Brain}
              title="Deep Learning Core"
              description="Advanced neural networks that evolve with your business"
              features={[
                "Multi-layer neural architecture",
                "Real-time weight adjustments",
                "Continuous model training",
                "Adaptive learning patterns"
              ]}
            />
            <ServiceCard
              icon={Route}
              title="Predictive Optimization"
              description="AI-driven route and resource optimization"
              features={[
                "Dynamic path finding",
                "Resource utilization forecasting",
                "Load optimization algorithms",
                "Real-time fleet management"
              ]}
            />
            <ServiceCard
              icon={LineChart}
              title="Intelligent Analytics"
              description="Deep insights powered by neural processing"
              features={[
                "Pattern recognition",
                "Anomaly detection",
                "Predictive maintenance",
                "Performance forecasting"
              ]}
            />
          </div>
        </div>
      </section>

      {/* Mobile Solutions Section */}
      <section className="py-20 bg-gradient-to-b from-purple-900 to-gray-900">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">Mobile Solutions</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Powerful mobile apps for drivers and operators on iOS and Android
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              <div className="grid grid-cols-1 gap-8">
                <ServiceCard
                  icon={Smartphone}
                  title="Driver App"
                  description="Comprehensive mobile solution for drivers"
                  features={[
                    "Load management and updates",
                    "Navigation and routing",
                    "Document scanning",
                    "Real-time communication"
                  ]}
                />
                <ServiceCard
                  icon={Cloud}
                  title="Cloud Sync"
                  description="Seamless data synchronization"
                  features={[
                    "Offline capability",
                    "Automatic background sync",
                    "Cross-device compatibility",
                    "Secure data storage"
                  ]}
                />
              </div>
            </div>
            
            <div className="order-1 md:order-2">
              <MobileAppPreview />
            </div>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className="bg-gray-900 border-t border-white/10">
        <div className="container mx-auto px-4 py-12 md:py-20">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-8 mb-12 md:mb-20">
            {/* Company Info */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">
                LoadLink
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Enterprise-grade logistics platform with real-time operations, custom-built infrastructure, and intelligent automation.
              </p>
              <div className="flex space-x-4">
                {/* Social Media Links */}
                {[
                  { icon: 'M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z', name: 'Twitter' },
                  { icon: 'M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z', name: 'LinkedIn' },
                  { icon: 'M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z', name: 'GitHub' }
                ].map((social) => (
                  <a
                    key={social.name}
                    href="#"
                    className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors duration-300"
                    aria-label={social.name}
                  >
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d={social.icon} />
                    </svg>
                  </a>
                ))}
              </div>
            </div>

            {/* Product Features */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-6">Product Features</h4>
              <ul className="space-y-4">
                {[
                  'Real-Time Operations',
                  'Load Management',
                  'Billing & Disputes',
                  'Document Control',
                  'Mobile Applications',
                  'Telematics Integration'
                ].map((item) => (
                  <li key={item}>
                    <Link
                      to={`/features/${item.toLowerCase().replace(/\s+/g, '-')}`}
                      className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center group"
                    >
                      <ArrowRight className="w-4 h-4 mr-2 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                      {item}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Enterprise */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-6">Enterprise</h4>
              <ul className="space-y-4">
                {[
                  'Security & Compliance',
                  'API Documentation',
                  'User Management',
                  'Custom Integration',
                  'Admin Controls',
                  'Analytics Dashboard'
                ].map((item) => (
                  <li key={item}>
                    <Link
                      to={`/enterprise/${item.toLowerCase().replace(/\s+/g, '-')}`}
                      className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center group"
                    >
                      <ArrowRight className="w-4 h-4 mr-2 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                      {item}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-6">Contact Us</h4>
              <ul className="space-y-4">
                <li>
                  <a href="tel:+1234567890" className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center">
                    <Phone className="w-4 h-4 mr-2" />
                    +1 (234) 567-890
                  </a>
                </li>
                <li>
                  <a href="mailto:enterprise@loadlink.com" className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center">
                    <Mail className="w-4 h-4 mr-2" />
                    enterprise@loadlink.com
                  </a>
                </li>
                <li className="flex items-start">
                  <MapPin className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-400">
                    Enterprise Solutions Division<br />
                    LoadLink Technologies<br />
                    San Francisco, CA 94105
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-white/10">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-gray-400 text-sm">
                © {new Date().getFullYear()} LoadLink. Enterprise-grade logistics platform.
              </div>
              <div className="flex space-x-6">
                {[
                  'Terms of Service', 
                  'Privacy Policy', 
                  'Security', 
                  'API Status'
                ].map((item) => (
                  <Link
                    key={item}
                    to={`/${item.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-gray-400 hover:text-white text-sm transition-colors duration-300"
                  >
                    {item}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Services;